// src/core/platform.ts
function browserPlatform() {
  const nav = typeof navigator !== "undefined" ? navigator : void 0;
  const locks = nav?.locks;
  return {
    fetch: (url, init) => globalThis.fetch(url, init),
    locks: locks && typeof locks.request === "function" ? locks : null,
    createChannel: (name) => typeof BroadcastChannel === "function" ? new BroadcastChannel(name) : null,
    document: typeof document !== "undefined" ? document : null,
    isOnline: () => nav?.onLine !== false,
    connectivity: typeof window !== "undefined" && typeof window.addEventListener === "function" ? window : null
  };
}

// src/core/assetCheck.ts
var BUILD_ID_META_NAME = "app-updater-build-id";
var META_TAG = /<meta\b[^>]*>/gi;
var NAME_ATTR = new RegExp(`\\bname\\s*=\\s*["']${BUILD_ID_META_NAME}["']`, "i");
var CONTENT_ATTR = /\bcontent\s*=\s*["']([^"']*)["']/i;
function readServedBuildId(html) {
  for (const tag of html.match(META_TAG) ?? []) {
    if (!NAME_ATTR.test(tag)) continue;
    const content = CONTENT_ATTR.exec(tag);
    return content ? content[1] : null;
  }
  return null;
}

// src/core/debug.ts
function createDebugLog(enabled) {
  if (!enabled) return () => {
  };
  return (event, data = {}) => {
    console.log(`[app-updater] ${event}`, data);
  };
}

// src/core/leader.ts
function createLeadership(platform, lockName, log) {
  let leader = false;
  let destroyed = false;
  const listeners = /* @__PURE__ */ new Set();
  const abort = new AbortController();
  let release;
  function setLeader(value) {
    if (value === leader) return;
    leader = value;
    log(value ? "leader-elected" : "leader-lost", { lockName });
    for (const listener of [...listeners]) {
      try {
        listener(value);
      } catch {
      }
    }
  }
  if (platform.locks) {
    platform.locks.request(lockName, { signal: abort.signal }, () => {
      if (destroyed) return Promise.resolve();
      setLeader(true);
      return new Promise((resolve) => {
        release = resolve;
      });
    }).catch(() => {
    });
  } else {
    log("no-web-locks", { lockName });
    setLeader(true);
  }
  return {
    isLeader: () => leader,
    onChange(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      listeners.clear();
      leader = false;
      abort.abort();
      release?.();
    }
  };
}

// src/core/manifest.ts
function isValidBuildId(value) {
  return typeof value === "string" && value.length > 0 && !/\s/.test(value);
}
function isValidClientVersion(value) {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function parseManifest(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const keys = Object.keys(value);
  if (keys.length !== 2) return null;
  const { buildId, clientVersion } = value;
  if (!isValidBuildId(buildId) || !isValidClientVersion(clientVersion)) return null;
  return { buildId, clientVersion };
}
function derivePolicy(bundle, manifest) {
  if (manifest.buildId === bundle.buildId) return null;
  return manifest.clientVersion > bundle.clientVersion ? "urgent" : "normal";
}
function isReloadOutcome(value) {
  if (typeof value !== "object" || value === null) return false;
  const { result, expectedBuildId, loadedBuildId, reloads } = value;
  return (result === "confirmed" || result === "mismatch") && isValidBuildId(expectedBuildId) && isValidBuildId(loadedBuildId) && typeof reloads === "number" && Number.isSafeInteger(reloads) && reloads >= 0;
}

// src/core/poller.ts
var DEFAULT_POLL_MS = 5 * 6e4;
var MIN_POLL_MS = 1e3;
var INITIAL_JITTER_MAX_MS = 3e4;
var BACKOFF_STEPS_MS = [3e4, 6e4, 12e4, 3e5, 6e5, 18e5];
function createPoller(config) {
  const { platform, versionUrl, pollIntervalMs, onManifest, onFailure, backoffMs = BACKOFF_STEPS_MS, log } = config;
  let running = false;
  let destroyed = false;
  let fetching = false;
  let timer;
  let failures = 0;
  async function check() {
    if (destroyed || fetching) return;
    if (!platform.isOnline()) {
      log("check-skipped-offline");
      return;
    }
    fetching = true;
    try {
      const res = await platform.fetch(versionUrl, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const manifest = parseManifest(await res.json());
      if (!manifest) throw new Error("malformed manifest");
      log("check-ok", { manifestBuildId: manifest.buildId, manifestClientVersion: manifest.clientVersion });
      if (!destroyed) await onManifest(manifest);
      failures = 0;
    } catch (error) {
      failures += 1;
      log("check-failed", { error: String(error), consecutive: failures, nextDelayMs: nextDelay() });
      if (!destroyed) onFailure?.({ error, consecutive: failures });
    } finally {
      fetching = false;
    }
  }
  function cancel() {
    if (timer !== void 0) {
      clearTimeout(timer);
      timer = void 0;
    }
  }
  function schedule(delay) {
    cancel();
    if (!running || destroyed) return;
    timer = setTimeout(async () => {
      timer = void 0;
      if (!running || destroyed) return;
      await check();
      schedule(nextDelay());
    }, delay);
  }
  function nextDelay() {
    return failures > 0 ? backoffMs[Math.min(failures, backoffMs.length) - 1] : pollIntervalMs;
  }
  function onVisibilityChange() {
    if (!running || destroyed) return;
    if (platform.document?.visibilityState !== "visible") return;
    if (failures > 0) {
      log("check-skipped-backoff", { consecutive: failures });
      return;
    }
    void checkNow();
  }
  function start() {
    if (running || destroyed) return;
    running = true;
    platform.document?.addEventListener("visibilitychange", onVisibilityChange);
    schedule(Math.floor(Math.random() * Math.min(INITIAL_JITTER_MAX_MS, pollIntervalMs)));
  }
  function stop() {
    running = false;
    cancel();
    platform.document?.removeEventListener("visibilitychange", onVisibilityChange);
  }
  async function checkNow() {
    if (destroyed) return;
    cancel();
    await check();
    schedule(nextDelay());
  }
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    stop();
  }
  return { start, stop, checkNow, destroy };
}

// src/core/retry.ts
var DEFAULT_MAX_ATTEMPTS = 3;
var DEFAULT_RETRY = Object.freeze({
  maxAttempts: DEFAULT_MAX_ATTEMPTS,
  backoffMs: Object.freeze([...BACKOFF_STEPS_MS])
});
function resolveRetry(options) {
  const maxAttempts = options?.maxAttempts ?? DEFAULT_RETRY.maxAttempts;
  const backoffMs = options?.backoffMs ?? DEFAULT_RETRY.backoffMs;
  if (!Number.isSafeInteger(maxAttempts) || maxAttempts < 1) {
    throw new Error("app-updater: retry.maxAttempts must be an integer, 1 or more");
  }
  if (!Array.isArray(backoffMs) || backoffMs.length === 0 || !backoffMs.every((ms) => Number.isFinite(ms) && ms >= 0)) {
    throw new Error("app-updater: retry.backoffMs must be a non-empty list of milliseconds, each 0 or more");
  }
  return { maxAttempts, backoffMs: [...backoffMs] };
}
function backoffAfter(policy, failures) {
  const i = Math.min(Math.max(failures, 1), policy.backoffMs.length) - 1;
  return policy.backoffMs[i];
}

// src/core/updater.ts
var DEFAULT_VERSION_URL = "/version.json";
var DEFAULT_CHANNEL_NAME = "hopdrive-app-updater";
var DEFAULT_URGENT_DEADLINE_MS = 3e5;
var ONLINE_RECHECK_DEBOUNCE_MS = 1e3;
var ERROR_REASONS = ["manifest", "asset-check", "chunk-load"];
var AssetCheckError = class extends Error {
  constructor(manifest, detail) {
    super(`asset check failed for ${manifest.buildId}: ${detail}`);
    this.manifest = manifest;
  }
};
function defaultIndexUrl(versionUrl) {
  const folder = versionUrl.split("?")[0].replace(/[^/]*$/, "");
  return folder || "/";
}
function createAppUpdaterOn(platform, config) {
  const {
    bundle: rawBundle,
    versionUrl = DEFAULT_VERSION_URL,
    channelName = DEFAULT_CHANNEL_NAME,
    pollIntervalMs = DEFAULT_POLL_MS,
    debug = false,
    onStateChange,
    urgentDeadlineMs = DEFAULT_URGENT_DEADLINE_MS,
    indexUrl = defaultIndexUrl(versionUrl)
  } = config;
  const retry = resolveRetry(config.retry);
  if (!rawBundle || !isValidBuildId(rawBundle.buildId) || !isValidClientVersion(rawBundle.clientVersion)) {
    throw new Error("app-updater: bundle needs a non-empty buildId without spaces and a non-negative integer clientVersion");
  }
  if (!Number.isFinite(pollIntervalMs) || pollIntervalMs < MIN_POLL_MS) {
    throw new Error(`app-updater: pollIntervalMs must be at least ${MIN_POLL_MS}`);
  }
  if (!Number.isFinite(urgentDeadlineMs) || urgentDeadlineMs < 0) {
    throw new Error("app-updater: urgentDeadlineMs must be a finite number of milliseconds, 0 or more");
  }
  const bundle = { buildId: rawBundle.buildId, clientVersion: rawBundle.clientVersion };
  const log = createDebugLog(debug);
  let destroyed = false;
  let state = { status: "current", bundle };
  let reloadOutcome = null;
  const listeners = /* @__PURE__ */ new Set();
  function setState(next) {
    state = reloadOutcome ? { ...next, lastReloadOutcome: reloadOutcome } : next;
    try {
      onStateChange?.(state);
    } catch {
    }
    for (const listener of [...listeners]) {
      try {
        listener(state);
      } catch {
      }
    }
  }
  let freshAt = 0;
  let wentOffline = false;
  let awaitingFresh = false;
  let recheckTimer;
  function isFresh() {
    return platform.isOnline() && !wentOffline && freshAt > 0 && Date.now() - freshAt <= pollIntervalMs;
  }
  function cancelRecheck() {
    if (recheckTimer !== void 0) clearTimeout(recheckTimer);
    recheckTimer = void 0;
  }
  function scheduleRecheck(why) {
    if (destroyed || recheckTimer !== void 0) return;
    recheckTimer = setTimeout(() => {
      recheckTimer = void 0;
      if (destroyed || !platform.isOnline()) return;
      log("recheck", { why, leader: leadership.isLeader() });
      if (leadership.isLeader()) void poller.checkNow();
      else if (awaitingFresh) post({ type: "check" });
    }, ONLINE_RECHECK_DEBOUNCE_MS);
  }
  function handleManifest(manifest) {
    if (destroyed) return;
    let rechecked = false;
    if (platform.isOnline()) {
      freshAt = Date.now();
      wentOffline = false;
      cancelRecheck();
      rechecked = awaitingFresh;
      awaitingFresh = false;
    }
    if (state.status === "error" && state.reason === "chunk-load" && manifest.buildId === state.buildId) return;
    const policy = derivePolicy(bundle, manifest);
    if (policy === null) {
      if (state.status !== "current") {
        log("back-to-current", { bundleBuildId: bundle.buildId });
        setState({ status: "current", bundle });
      }
      return;
    }
    if (state.status === "pending" && state.manifest.buildId === manifest.buildId && state.manifest.clientVersion === manifest.clientVersion) {
      if (rechecked) {
        log("recheck-ok", { manifestBuildId: manifest.buildId });
        setState({ ...state });
      }
      return;
    }
    log("update-found", {
      bundleBuildId: bundle.buildId,
      manifestBuildId: manifest.buildId,
      bundleClientVersion: bundle.clientVersion,
      manifestClientVersion: manifest.clientVersion,
      policy
    });
    const detectedAt = Date.now();
    const deadline = policy !== "urgent" ? null : state.status === "pending" && state.deadline !== null ? state.deadline : detectedAt + urgentDeadlineMs;
    setState({ status: "pending", bundle, manifest, policy, detectedAt, deadline });
  }
  function applyFailure(failure) {
    if (destroyed || state.status === "error") return;
    if (state.status === "pending" && (failure.reason === "manifest" || failure.reason === "asset-check" && failure.buildId !== state.manifest.buildId)) {
      log("check-failed-keeping-pending", { reason: failure.reason, buildId: failure.buildId, attempts: failure.attempts });
      return;
    }
    if (failure.reason === "manifest") {
      log("check-failed-quiet", { attempts: failure.attempts });
      return;
    }
    log("gave-up", { reason: failure.reason, buildId: failure.buildId, attempts: failure.attempts });
    setState({ status: "error", bundle, ...failure });
  }
  let verifiedBuildId = null;
  async function verifyServed(manifest) {
    if (manifest.buildId === bundle.buildId || manifest.buildId === verifiedBuildId) return;
    let served;
    try {
      const res = await platform.fetch(indexUrl, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (typeof res.text !== "function") return;
      served = readServedBuildId(await res.text());
    } catch (error) {
      throw new AssetCheckError(manifest, String(error));
    }
    if (served === null) {
      log("asset-check-unverifiable", { manifestBuildId: manifest.buildId });
    } else if (served !== manifest.buildId) {
      throw new AssetCheckError(manifest, `index.html serves ${served}`);
    } else {
      log("asset-check-ok", { manifestBuildId: manifest.buildId });
    }
    verifiedBuildId = manifest.buildId;
  }
  let channel = null;
  try {
    channel = platform.createChannel(channelName);
  } catch {
    channel = null;
  }
  function post(message) {
    try {
      channel?.postMessage(message);
    } catch {
    }
  }
  function onMessage(event) {
    if (destroyed) return;
    const message = event.data;
    if (!message || typeof message !== "object") return;
    if (message.type === "manifest") {
      const manifest = parseManifest(message.manifest);
      if (manifest) handleManifest(manifest);
    } else if (message.type === "error") {
      const failure = parseFailure(message.failure);
      if (failure) applyFailure(failure);
    } else if (message.type === "check" && leadership.isLeader()) {
      void poller.checkNow();
    }
  }
  channel?.addEventListener("message", onMessage);
  const poller = createPoller({
    platform,
    versionUrl,
    pollIntervalMs,
    log,
    backoffMs: retry.backoffMs,
    async onManifest(manifest) {
      await verifyServed(manifest);
      handleManifest(manifest);
      post({ type: "manifest", manifest });
    },
    onFailure({ error, consecutive }) {
      if (consecutive < retry.maxAttempts) return;
      const failure = error instanceof AssetCheckError ? { reason: "asset-check", buildId: error.manifest.buildId, attempts: consecutive, manifest: error.manifest } : { reason: "manifest", buildId: bundle.buildId, attempts: consecutive, manifest: null };
      const before = state;
      applyFailure(failure);
      if (state !== before) post({ type: "error", failure });
    }
  });
  const leadership = createLeadership(
    { locks: channel ? platform.locks : null },
    `${channelName}:leader`,
    log
  );
  leadership.onChange((isLeader) => {
    if (destroyed) return;
    if (isLeader) poller.start();
    else poller.stop();
  });
  if (leadership.isLeader()) poller.start();
  const onOffline = () => {
    if (destroyed) return;
    wentOffline = true;
    log("offline", { pending: state.status === "pending" });
  };
  const onOnline = () => {
    if (destroyed) return;
    log("online");
    scheduleRecheck("online");
  };
  const onVisibility = () => {
    if (destroyed || platform.document?.visibilityState !== "visible" || leadership.isLeader()) return;
    if (state.status === "pending" && !isFresh()) {
      awaitingFresh = true;
      scheduleRecheck("resumed");
    }
  };
  platform.connectivity?.addEventListener("offline", onOffline);
  platform.connectivity?.addEventListener("online", onOnline);
  platform.document?.addEventListener("visibilitychange", onVisibility);
  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    isLeader: () => leadership.isLeader(),
    async checkNow() {
      if (destroyed) return;
      if (leadership.isLeader()) await poller.checkNow();
      else post({ type: "check" });
    },
    ensureFresh() {
      if (destroyed) return false;
      if (isFresh()) return true;
      if (!awaitingFresh) {
        log("not-fresh", {
          online: platform.isOnline(),
          wentOffline,
          ageMs: freshAt > 0 ? Date.now() - freshAt : null
        });
      }
      awaitingFresh = true;
      scheduleRecheck("not-fresh");
      return false;
    },
    reportReloadOutcome(outcome) {
      if (!isReloadOutcome(outcome)) throw new Error("app-updater: reportReloadOutcome() needs a result, two valid build ids and a reload count");
      if (destroyed) return;
      const { result, expectedBuildId, loadedBuildId, reloads } = outcome;
      reloadOutcome = { result, expectedBuildId, loadedBuildId, reloads };
      setState(state);
    },
    fail(reason, detail) {
      const failure = parseFailure({
        reason,
        buildId: detail?.buildId,
        attempts: detail?.attempts,
        manifest: state.status === "pending" ? state.manifest : null
      });
      if (!failure) throw new Error("app-updater: fail() needs a known reason, a valid buildId and a whole number of attempts");
      applyFailure(failure);
    },
    log,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      cancelRecheck();
      platform.connectivity?.removeEventListener("offline", onOffline);
      platform.connectivity?.removeEventListener("online", onOnline);
      platform.document?.removeEventListener("visibilitychange", onVisibility);
      poller.destroy();
      leadership.destroy();
      if (channel) {
        channel.removeEventListener("message", onMessage);
        channel.close();
        channel = null;
      }
      listeners.clear();
    }
  };
}
function parseFailure(value) {
  if (typeof value !== "object" || value === null) return null;
  const { reason, buildId, attempts, manifest } = value;
  if (!ERROR_REASONS.includes(reason)) return null;
  if (!isValidBuildId(buildId)) return null;
  if (typeof attempts !== "number" || !Number.isSafeInteger(attempts) || attempts < 0) return null;
  const parsed = manifest == null ? null : parseManifest(manifest);
  if (manifest != null && !parsed) return null;
  return { reason, buildId, attempts, manifest: parsed };
}

// src/index.ts
function createAppUpdater(config) {
  return createAppUpdaterOn(browserPlatform(), config);
}
var APP_UPDATER_CORE = Object.freeze({ layer: "core" });

export {
  BUILD_ID_META_NAME,
  parseManifest,
  derivePolicy,
  DEFAULT_POLL_MS,
  MIN_POLL_MS,
  DEFAULT_MAX_ATTEMPTS,
  DEFAULT_RETRY,
  resolveRetry,
  backoffAfter,
  DEFAULT_VERSION_URL,
  DEFAULT_CHANNEL_NAME,
  DEFAULT_URGENT_DEADLINE_MS,
  ONLINE_RECHECK_DEBOUNCE_MS,
  createAppUpdater,
  APP_UPDATER_CORE
};
//# sourceMappingURL=chunk-VK56FKEY.mjs.map